<template>
  <BittsModal
    class="c-decline-reason-modal"
    :width="516"
    primary-button-text="Confirm Decline"
    secondary-button-text="Cancel"
    primary-button-type="danger"
    :action-loading="loading"
    :disabled="!selectedReason || noteTooLong"
    :use-mask-to-close="true"
    data-testid="decline-reason-modal"
    @action="handleConfirm"
    @secondary-action="handleClose"
    @closed="handleClose"
  >
    <template #header>
      <div class="c-decline-reason-modal__header">
        <BittsAvatarStack
          v-if="partner"
          :org-list="[
            currentOrgWithInitials,
            { ...partner, showInitials: true },
          ]"
          :transparent="true"
          :show-initials="true"
          size="medium"
        />
        <h2 class="c-decline-reason-modal__title">
          Decline partnership with {{ partnerName }}
        </h2>
      </div>
    </template>
    <div class="flex flex-col gap-16">
      <BittsRadioGroupCards
        form-label="Reason"
        orientation="vertical"
        :options="DECLINE_REASONS"
        :initial-value="selectedReason"
        data-testid="decline-reason-options"
        @change="handleReasonChange"
      >
        <template #label="{ option }">
          <span>{{ (option as DeclineReasonOption).label }}</span>
          <span
            v-if="
              (option as DeclineReasonOption).value ===
              DECLINE_REASON_PREFER_NOT_TO_SAY
            "
            class="c-decline-reason-modal__hint"
          >
            No reason will be sent to the recipient
          </span>
        </template>
      </BittsRadioGroupCards>
      <BittsTextArea
        v-model="note"
        :form-label="{ title: 'Additional notes', secondaryText: 'Optional' }"
        placeholder="Add a note to share with your partner"
        data-testid="decline-note-input"
        :disabled="isPreferNotToSay"
        :status="noteTooLong ? 'danger' : 'default'"
        :danger-text="`Your note may not exceed ${MAX_DECLINE_NOTE_LENGTH} characters`"
      />
    </div>
  </BittsModal>
</template>

<script setup lang="ts">
import {
  BittsAvatarStack,
  BittsModal,
  BittsRadioGroupCards,
  BittsTextArea,
} from '@crossbeam/bitts';

import { computed, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import {
  DECLINE_REASONS,
  DECLINE_REASON_PREFER_NOT_TO_SAY,
  type DeclineReasonOption,
  type DeclineReasonType,
  MAX_DECLINE_NOTE_LENGTH,
} from '@/constants/decline_reasons';

const {
  partnerName = '',
  partner = null,
  loading = false,
} = defineProps<{
  partnerName?: string;
  partner?: {
    name?: string;
    logo_url?: string | null;
    clearbit_domain?: string | null;
    domain?: string | null;
  } | null;
  loading?: boolean;
}>();

const emit = defineEmits<{
  confirm: [
    payload: { decline_reason: DeclineReasonType; decline_note: string | null },
  ];
  close: [];
}>();

const { currentOrg } = useAuth();
const currentOrgWithInitials = computed(() => ({
  ...currentOrg.value,
  showInitials: true,
}));

const selectedReason = ref<DeclineReasonType | undefined>(undefined);
const note = ref<string>('');

const isPreferNotToSay = computed(
  () => selectedReason.value === DECLINE_REASON_PREFER_NOT_TO_SAY,
);
const noteTooLong = computed(() => note.value.length > MAX_DECLINE_NOTE_LENGTH);

function handleReasonChange(value: DeclineReasonType) {
  selectedReason.value = value;
  if (value === DECLINE_REASON_PREFER_NOT_TO_SAY) {
    note.value = '';
  }
}

function handleConfirm() {
  if (!selectedReason.value || noteTooLong.value) return;
  emit('confirm', {
    decline_reason: selectedReason.value,
    decline_note: note.value.trim() || null,
  });
}

function handleClose() {
  emit('close');
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-decline-reason-modal.o-bitts-modal.bitts-modal--primary
  :deep(.o-bitts-modal__content) {
  @apply m-0 p-16;
}

.c-decline-reason-modal__header {
  @apply flex flex-col items-center gap-16 w-full;
}

/* Ant Design overrides, important unavoidable here */
.c-decline-reason-modal__header :deep(.ant-avatar) {
  width: 48px !important;
  height: 48px !important;
  line-height: 48px !important;
}
.c-decline-reason-modal__title {
  @apply text-neutral-text-strong text-xl font-bold text-center;
}

.c-decline-reason-modal__hint {
  @apply text-neutral-text text-sm italic ml-4;
}
</style>
